import { useEffect, useRef } from 'react';

// Buffer over the accordionContent grid-template-rows transition (0.3s) so the
// scroll runs after the expand animation has fully settled and the element has
// reached its final height.
const DEFAULT_EXPAND_ANIMATION_MS = 350;

/**
 * When `isOpen` becomes true, scrolls the referenced element into view once the
 * expand animation settles. On short (landscape) screens the last accordion
 * expands below the visible area, so this brings its content into the scrollable
 * container. No-op when the element is already visible.
 *
 * @param isOpen  whether the element is currently expanded
 * @param delayMs how long to wait before scrolling (should cover the expand animation)
 */
export const useScrollIntoViewOnOpen = <T extends HTMLElement = HTMLDivElement>(
  isOpen: boolean,
  delayMs: number = DEFAULT_EXPAND_ANIMATION_MS,
) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, delayMs);

    return () => clearTimeout(timer);
  }, [isOpen, delayMs]);

  return ref;
};
